import type { DocumentType, PackCode, RequestType } from './enums.js';

export type QualificationField =
  | { id: string; label: string; type: 'text'; required: boolean }
  | { id: string; label: string; type: 'number'; required: boolean; min?: number; max?: number }
  | { id: string; label: string; type: 'boolean'; required: boolean }
  | {
      id: string;
      label: string;
      type: 'select';
      required: boolean;
      options: { value: string; label: string }[];
    };

export type QualificationConfig = {
  label: string;
  description: string;
  fields: QualificationField[];
  requiredDocuments: DocumentType[];
  suggestedPacks: PackCode[];
};

export const qualificationCatalog: Record<RequestType, QualificationConfig> = {
  etude_raccordement: {
    label: 'Étude de raccordement fibre',
    description:
      "Analyse de faisabilité du raccordement à la fibre optique pour votre adresse.",
    fields: [
      { id: 'urgent', label: 'Demande urgente ?', type: 'boolean', required: true },
      { id: 'distanceVoirie', label: 'Distance à la voirie (m)', type: 'number', required: false, min: 0 },
    ],
    requiredDocuments: ['piece_identite', 'attestation_adressage'],
    suggestedPacks: ['serenite_fibre'],
  },
  point_raccordement: {
    label: 'Demande de point de raccordement (PAR / PBO)',
    description: 'Création ou repositionnement de PAR ou PBO sur votre site.',
    fields: [
      {
        id: 'parOrPbo',
        label: 'Type de point souhaité',
        type: 'select',
        required: true,
        options: [
          { value: 'par', label: 'Point d’aboutement de raccordement (PAR)' },
          { value: 'pbo', label: 'Point de branchement optique (PBO)' },
        ],
      },
    ],
    requiredDocuments: ['piece_identite', 'plans_architecte'],
    suggestedPacks: ['confort_fibre'],
  },
  travaux_fibre: {
    label: 'Travaux fibre / génie civil',
    description: 'Travaux de génie civil pour la mise en place de la fibre.',
    fields: [
      { id: 'lineaireMetres', label: 'Linéaire estimé (m)', type: 'number', required: true, min: 0 },
      { id: 'aerienOuSouterrain', label: 'Aérien ?', type: 'boolean', required: true },
    ],
    requiredDocuments: ['piece_identite', 'permis_construire', 'plans_architecte', 'photo_exterieur'],
    suggestedPacks: ['confort_fibre'],
  },
  previsite_technique: {
    label: 'Pré-visite technique (technicien)',
    description: 'Intervention d’un technicien pour qualifier le site.',
    fields: [
      { id: 'creneauPrefere', label: 'Créneau préféré', type: 'text', required: false },
    ],
    requiredDocuments: ['piece_identite', 'photo_interieur', 'photo_exterieur'],
    suggestedPacks: ['serenite_fibre'],
  },
  reparation_sav: {
    label: 'Réparation / SAV',
    description: 'Intervention de maintenance ou réparation sur une installation existante.',
    fields: [
      { id: 'naturePanne', label: 'Nature de la panne', type: 'text', required: true },
    ],
    requiredDocuments: ['piece_identite', 'photo_installation'],
    suggestedPacks: ['essentiel_fibre'],
  },
  devis_travaux_divers: {
    label: 'Demande de devis travaux divers',
    description: 'Devis pour des travaux annexes (déplacement câbles, etc.).',
    fields: [
      { id: 'descriptionTravaux', label: 'Description', type: 'text', required: true },
    ],
    requiredDocuments: ['piece_identite'],
    suggestedPacks: ['confort_fibre'],
  },
  securisation_fibre: {
    label: 'Sécurisation installation fibre',
    description: 'Sécurisation de votre installation fibre (mise en conformité).',
    fields: [
      { id: 'risqueIdentifie', label: 'Risque identifié', type: 'text', required: false },
    ],
    requiredDocuments: ['piece_identite', 'photo_installation'],
    suggestedPacks: ['essentiel_fibre'],
  },
  enfouissement_cables: {
    label: 'Enfouissement de câbles',
    description: 'Travaux d’enfouissement de câbles aériens.',
    fields: [
      { id: 'lineaireMetres', label: 'Linéaire estimé (m)', type: 'number', required: true, min: 0 },
    ],
    requiredDocuments: ['piece_identite', 'permis_construire', 'plans_architecte'],
    suggestedPacks: ['confort_fibre'],
  },
};

export function getQualificationConfig(type: RequestType): QualificationConfig {
  return qualificationCatalog[type];
}
